import { Resolvers, TypePolicies } from '@apollo/client';
import { relayStylePagination } from '@apollo/client/utilities';
import { unescapeHtml } from '@sortlist-frontend/utils';

import { LocalizedTextType } from '../core-types';

type Content = {
  originalDescriptions: LocalizedTextType[];
  originalTaglines: LocalizedTextType[];
  aiDescriptions: LocalizedTextType[];
  aiTaglines: LocalizedTextType[];
};

// `Opportunity.messages` and `Nominee.messages` are each read two ways: a paginated thread shape
// (`edges`/`pageInfo`) and a lean preview shape (`nodes`, always `last: 1`, used by room lists,
// notification bells and inbox/shortlist fragments). `relayStylePagination`'s default
// `keyArgs: false` keys the cache by field name alone regardless of alias, so the two shapes
// collided in one slot — aliasing the preview query doesn't change this, since Apollo resolves
// field policies by the real GraphQL field name, never the response alias. Partition on
// `last === 1` (a signature no thread fetch ever uses) so the two shapes get distinct cache slots
// instead of colliding.
const messagesPreviewKeyArgs = (args: Record<string, unknown> | null) => (args?.last === 1 ? 'lastMessage' : false);

export const buildMultilingualContent = (content: Content) => {
  const { originalDescriptions, aiDescriptions, originalTaglines, aiTaglines } = content ?? {};

  const multilingualContent: {
    [key: string]: { description: string; tagline: string; isAiActivated: boolean };
  } = {};
  const groupedDescriptions = [...originalDescriptions, ...aiDescriptions];
  const groupedTaglines = [...originalTaglines, ...aiTaglines];

  groupedDescriptions.forEach((description) => {
    const tagline = groupedTaglines.find((tagline) => tagline.locale === description.locale);
    if (multilingualContent[description.locale] != null) return;

    multilingualContent[description.locale] = {
      description: description.text ?? '',
      tagline: unescapeHtml(tagline?.text ?? ''),
      isAiActivated: aiDescriptions.some((aiDescription) => aiDescription.locale === description.locale),
    };
  });

  return multilingualContent;
};

/**
 * You can add here any implementation of mock fields for existing entities
 * https://www.apollographql.com/docs/react/development-testing/client-schema-mocking
 */
export const customTypePolicies: TypePolicies = {
  Balance: {
    fields: {
      balanceItemTypeEnum: {
        read(_, { readField }) {
          const itemType = readField('itemType') as string | undefined;
          return itemType?.toUpperCase();
        },
      },
      balanceModuleScopeEnum: {
        read(_, { readField }) {
          const moduleScope = readField('moduleScope') as string | undefined;
          return moduleScope?.toUpperCase();
        },
      },
    },
  },
  Review: {
    fields: {
      isRecentlyRecommended: {
        read(_, { readField }) {
          const recommended = readField('recommended') as boolean;
          const createdAt = readField('createdAt') as Date;

          if (!recommended) return false;

          const cutOffDate = new Date().setMonth(new Date().getMonth() - 18);
          return new Date(createdAt) > new Date(cutOffDate);
        },
      },
    },
  },
  User: {
    fields: {
      fullName: {
        read(_, { readField }) {
          // If you need `name` for your computed property
          // you will need to request it on the query
          const firstName = readField('firstName') as string;
          const lastName = readField('lastName') as string;
          return `${firstName} ${lastName}`;
        },
      },
      projects: relayStylePagination(),
    },
  },
  MinimalUser: {
    fields: {
      fullName: {
        read(_, { readField }) {
          // If you need `name` for your computed property
          // you will need to request it on the query
          const firstName = readField('firstName') as string;
          const lastName = readField('lastName') as string;
          return `${firstName} ${lastName}`;
        },
      },
    },
  },
  AiWorkflowRun: {
    fields: {
      isDone: {
        read(_, { readField }) {
          // const createdAt = readField('createdAt') as Date;
          // const updatedAt = readField('updatedAt') as Date;
          const output = readField('output') as { __typename: string } | undefined;
          return output != null;
        },
      },
    },
  },
  RadarSession: {
    fields: {
      events: relayStylePagination(),
    },
  },
  Nominee: {
    fields: {
      messages: relayStylePagination(messagesPreviewKeyArgs),
    },
  },
  Opportunity: {
    fields: {
      messages: relayStylePagination(messagesPreviewKeyArgs),
    },
  },
  Project: {
    fields: {
      nominees: relayStylePagination(['reasons', 'withoutReasons', 'withMessages']),
    },
  },
  Query: {
    fields: {
      opportunities: relayStylePagination([
        'agencyId',
        'tab',
        'reasons',
        'briefingType',
        'withMessages',
        'appliedAfter',
      ]),
      nominees: relayStylePagination(['withMessages']),
    },
  },
  Agency: {
    fields: {
      multilingualContent: {
        read(_, { readField }) {
          const originalDescriptions = readField('originalDescriptions') as LocalizedTextType[];
          const originalTaglines = readField('originalTaglines') as LocalizedTextType[];
          const aiDescriptions = readField('aiDescriptions') as LocalizedTextType[];
          const aiTaglines = readField('aiTaglines') as LocalizedTextType[];

          return buildMultilingualContent({
            originalDescriptions: originalDescriptions ?? [],
            aiDescriptions: aiDescriptions ?? [],
            originalTaglines: originalTaglines ?? [],
            aiTaglines: aiTaglines ?? [],
          });
        },
      },
    },
  },
  Company: {
    fields: {
      // SeatUsage is an embedded value object with no id, and different queries
      // select different subsets of its fields (e.g. `{ used }` vs
      // `{ used, limit, available, canInvite }`). Without a merge policy Apollo
      // would replace the cached object wholesale and drop the unselected fields,
      // emitting a "Cache data may be lost" warning. Shallow-merge instead so the
      // superset is preserved across queries.
      seatUsage: {
        merge: true,
      },
    },
  },
  BillingConsumptionConnection: {
    merge: true,
  },
};

/**
 * You can add here any implementation of mock queries and mutations
 * Useful if the BE isn't ready yet
 * e.g. https://www.apollographql.com/docs/react/local-state/local-resolvers#async-local-resolvers
 */
export const customResolvers: Resolvers = {
  Mutation: {
    updateMultilingualContent: (_, { agencyId, multilingualContentArgs }) => {
      return {
        ...multilingualContentArgs,
        agencyId,
      };
    },
  },
};
